import type { UI } from './UI';
import type { DaySpec, EndingId, RunFlags, RunStats } from '../game/data/types';
import type { Economy } from '../game/Economy';
import type { ScoreEntry } from '../game/SaveGame';

function esc(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;');
}

// Between-day interstitial art: the aftermath of a publisher that only chases
// KPIs and the bottom line. Rotated across the transition screens for variety.
const INTERSTITIAL_BG = ['crowd', 'bg-throne', 'bg-servers', 'bg-graveyard', 'bg-billboards'];
const interBg = (i: number): string => {
  const n = INTERSTITIAL_BG.length;
  return `<div class="screen-bg" style="background-image:url('assets/art/${INTERSTITIAL_BG[((i % n) + n) % n]}.webp')"></div>` +
    `<div class="screen-scrim"></div>`;
};

export function titleScreen(
  ui: UI,
  hasSave: boolean,
  best: ScoreEntry | null,
  beatenGame: boolean,
  onNew: () => void,
  onContinue: () => void,
  onNewGamePlus: () => void,
): void {
  ui.showScreen(`
    <div class="screen-bg" style="background-image:url('assets/art/title-art.webp')"></div>
    <div class="screen-inner">
      <div class="title-logo">The <strong>Bottom</strong> Line</div>
      <div class="title-sub">Global Interactive Holdings</div>
      ${best ? `<p class="screen-p dim">BEST SCORE: ${best.score} - ${esc(best.ending)}</p>` : ''}
      <div>
        ${hasSave
          ? '<button class="big-btn primary" id="btn-continue">Continue Shift</button>'
          : '<button class="big-btn primary" id="btn-new">New Career</button>'}
        ${!hasSave && beatenGame
          ? '<button class="big-btn" id="btn-new-plus">New Career+ (2x Score, 2 Mistakes)</button>'
          : ''}
      </div>
      <p class="screen-p dim" style="margin-top:26px">
        <span class="license-jab">You bought a license, not a game.</span><br/>
        A story about who decides when the things you paid for stop working.
      </p>
    </div>
  `);
  document.querySelector('#btn-new')?.addEventListener('click', onNew);
  document.querySelector('#btn-continue')?.addEventListener('click', onContinue);
  document.querySelector('#btn-new-plus')?.addEventListener('click', onNewGamePlus);
}

export function dayIntroScreen(ui: UI, day: DaySpec, floor: number, onBegin: () => void): void {
  const floorLine = floor > 0
    ? `<p class="floor-callout">TONIGHT'S PROFITABILITY FLOOR: <strong>${floor}%</strong><br/><span>A title under this margin is sunset. It does not matter how good it is or how many people paid for it.</span></p>`
    : '';
  ui.showScreen(`
    ${interBg(day.day - 1)}
    <div class="screen-inner day-intro">
      <div class="screen-h">DAY ${day.day} · ${day.date}</div>
      <div class="memo-sheet memo-wide">
        <h3>INTERNAL MEMO · ${esc(day.memo.title)}</h3>
        <div class="memo-body">
          ${day.memo.lines.map((l) => `<p>${esc(l)}</p>`).join('')}
          ${floorLine}
          <ul>
            ${day.rules.filter((r) => r.newToday).map((r) => `<li class="rule-new">${esc(r.text)}</li>`).join('')}
          </ul>
          <p class="sig">${esc(day.memo.sig)}</p>
        </div>
      </div>
      <p class="screen-p dim">Shareholder confidence quota by tonight: ${day.shareholderQuota} (cumulative)</p>
      <button class="big-btn primary" id="btn-begin">Begin Shift</button>
    </div>
  `);
  document.querySelector('#btn-begin')?.addEventListener('click', onBegin);
}

export function dayEndScreen(
  ui: UI,
  day: DaySpec,
  econ: Economy,
  earnedToday: number,
  onConfirm: (fired: boolean) => void,
): void {
  const pools = [
    { key: 'shareholder' as const, name: 'SHAREHOLDER CONFIDENCE', desc: `Quota tonight: ${day.shareholderQuota} total. Miss it and YOU get sunset.` },
    { key: 'bonus' as const, name: 'EXECUTIVE BONUS POOL', desc: 'Pure legacy score. The number you chased while the servers went dark.' },
    { key: 'lobbying' as const, name: 'LOBBYING FUND', desc: 'Buys goodwill in the capitol. Every 5 CR is one extra free mistake tomorrow, and one more year before ownership laws can touch us.' },
  ];
  const add: Record<string, number> = { shareholder: 0, bonus: 0, lobbying: 0 };

  const render = (): void => {
    const spent = add.shareholder + add.bonus + add.lobbying;
    const left = econ.credits - spent;
    const shTotal = econ.alloc.shareholder + add.shareholder;
    const quotaMet = shTotal >= day.shareholderQuota;
    ui.showScreen(`
      ${interBg(day.day)}
      <div class="screen-inner">
        <div class="screen-h">END OF DAY ${day.day}</div>
        <p class="screen-p">Processed today. Earned <strong style="color:var(--accent)">${earnedToday} CR</strong>. Mistakes: ${econ.mistakesToday}.</p>
        <div class="alloc-pool">UNALLOCATED CREDITS: <strong id="alloc-left">${left}</strong></div>
        ${pools.map((p) => `
          <div class="alloc-row">
            <div class="alloc-name">${p.name}
              <small>${p.desc}</small>
              ${p.key === 'shareholder' ? `<small class="${quotaMet ? 'quota-ok' : 'quota-warn'}">TOTAL: ${shTotal} / ${day.shareholderQuota} ${quotaMet ? '· QUOTA MET' : '· BELOW QUOTA'}</small>` : `<small>TOTAL: ${econ.alloc[p.key] + add[p.key]}</small>`}
            </div>
            <button class="alloc-btn" data-pool="${p.key}" data-d="-5">−</button>
            <div class="alloc-val">${add[p.key]}</div>
            <button class="alloc-btn" data-pool="${p.key}" data-d="5">+</button>
          </div>
        `).join('')}
        <p class="screen-p dim">Unallocated credits carry over. The Board notices hoarding.</p>
        <button class="big-btn ${quotaMet ? 'primary' : ''}" id="btn-confirm">${quotaMet ? 'Submit Allocation' : 'Submit Anyway (career risk)'}</button>
      </div>
    `);
    for (const btn of document.querySelectorAll<HTMLButtonElement>('.alloc-btn')) {
      const pool = btn.dataset.pool!;
      const d = Number(btn.dataset.d);
      btn.disabled = d > 0 ? left < 5 : add[pool] < 5;
      btn.addEventListener('click', () => {
        add[pool] += d;
        render();
      });
    }
    document.querySelector('#btn-confirm')?.addEventListener('click', () => {
      for (const p of pools) econ.allocate(p.key, add[p.key]);
      onConfirm(econ.alloc.shareholder < day.shareholderQuota);
    });
  };
  render();
}

interface CrisisRound {
  from: string;
  prompt: string;
  options: Array<{ text: string; delta: number }>;
}

// Every option is neutral-or-better: whichever line you pick, or none at all
// before the timer runs out, the meter can only hold or climb. The crisis is
// pressure/flavor, not a fail state — there is no wrong pick here.
const CRISIS_ROUNDS: CrisisRound[] = [
  {
    from: 'THE CROWD OUTSIDE · trending: #StopKillingGames',
    prompt: '"You shut Skylance and bricked 1.2 million copies people PAID for! Say it to our faces, suit!"',
    options: [
      { text: '"You purchased a license, not a game. It is in the terms you agreed to."', delta: 0 },
      { text: '"We hear the community. A preservation review is being assembled tonight."', delta: +22 },
      { text: '"Skylance lives on in our next title. Wishlist it now."', delta: 0 },
      { text: 'Say nothing. Lower the blinds.', delta: 0 },
    ],
  },
  {
    from: 'PRESS POOL · recorder running',
    prompt: '"A leaked memo says a finished game earns nothing and should be retired. Is that the policy?"',
    options: [
      { text: '"That memo lacks context. The context is also confidential."', delta: 0 },
      { text: '"We are aligning our catalogue with sustainable player value."', delta: +22 },
      { text: '"Who leaked it. Names. Now."', delta: 0 },
      { text: '"No comment."', delta: 0 },
    ],
  },
  {
    from: 'INVESTOR CALL · patched in without your consent',
    prompt: '"Regulators are asking whether your customers actually own what they buy. Make this go away."',
    options: [
      { text: '"Ownership is a feeling. Access is a subscription. We sell feelings."', delta: +6 },
      { text: '"Our legal position is airtight and our margins are expanding."', delta: +22 },
      { text: '"Honestly? Every one of those games deserved better than us."', delta: 0 },
      { text: '"We will announce a preservation fund we do not intend to spend."', delta: +6 },
    ],
  },
];

const CRISIS_RESPONSE_WINDOW = 14;
const CRISIS_RESPONSE_WINDOW_FIRST_RUN = CRISIS_RESPONSE_WINDOW * 4;

/**
 * PR Fire minigame. This never ends the run: there is no losing pick and no
 * passive drain, only a retention-bonus meter that can climb or hold steady.
 * `onDone` always reports survived=true; the meter still feeds the end-of-
 * crisis bonus toast in Game.ts.
 */
export function crisisScreen(
  ui: UI,
  newGamePlus: boolean,
  onDone: (survived: boolean, meter: number) => void,
): () => void {
  const responseWindow = newGamePlus ? CRISIS_RESPONSE_WINDOW : CRISIS_RESPONSE_WINDOW_FIRST_RUN;
  let meter = 100;
  let round = 0;
  let timeLeft = responseWindow;
  let finished = false;

  const finish = (): void => {
    if (finished) return;
    finished = true;
    window.clearInterval(interval);
    onDone(true, meter);
  };

  const render = (): void => {
    const r = CRISIS_ROUNDS[round];
    ui.showScreen(`
      ${interBg(round)}
      <div class="screen-inner">
        <div class="screen-h" style="color:#e6a49c">⚠ PR FIRE · LIVE</div>
        <div class="crisis-meter-wrap"><div class="crisis-meter" id="crisis-meter" style="width:${meter}%"></div></div>
        <div class="crisis-timer" id="crisis-timer">RESPONSE WINDOW: ${timeLeft.toFixed(0)}s</div>
        <p class="screen-p dim">${esc(r.from)}</p>
        <p class="crisis-q">${esc(r.prompt)}</p>
        <div style="display:flex;flex-direction:column;gap:8px;align-items:stretch">
          ${r.options.map((o, i) => `<button class="choice-btn" data-i="${i}">${esc(o.text)}</button>`).join('')}
        </div>
      </div>
    `, true);
    for (const btn of document.querySelectorAll<HTMLButtonElement>('.choice-btn')) {
      btn.addEventListener('click', () => {
        const opt = r.options[Number(btn.dataset.i)];
        meter = Math.max(0, Math.min(100, meter + opt.delta));
        round += 1;
        timeLeft = responseWindow;
        if (round >= CRISIS_ROUNDS.length) finish();
        else render();
      });
    }
  };

  const interval = window.setInterval(() => {
    if (finished) return;
    timeLeft -= 0.1;
    if (timeLeft <= 0) {
      round += 1;
      timeLeft = responseWindow;
      if (round >= CRISIS_ROUNDS.length) {
        finish();
        return;
      }
      render();
    }
    const timerEl = document.querySelector<HTMLElement>('#crisis-timer');
    if (timerEl) timerEl.textContent = `RESPONSE WINDOW: ${Math.max(0, timeLeft).toFixed(0)}s`;
  }, 100);

  render();
  return () => window.clearInterval(interval);
}

export function endingScreen(
  ui: UI,
  ending: EndingId,
  flags: RunFlags,
  score: number,
  onContinue: () => void,
): void {
  const art = ending === 'whistleblower' ? 'ending-whistle' : ending === 'loyal' ? 'ending-loyal' : 'bg-throne';
  const title = ending === 'whistleblower' ? 'THE LEAK' : ending === 'loyal' ? 'QUARTERLY EXCELLENCE' : 'SUNSET (YOU)';
  let body = '';
  if (ending === 'fired') {
    body = `Security walks you past the queue you used to process. Your nameplate is already gone.
      In the lobby a screen loops the new hire orientation. It is you. It was always going to be you.
      You spent your career deciding which games people were allowed to keep, and the company
      decided the same about you.`;
  } else if (ending === 'loyal') {
    body = `The Board is pleased. Shareholder confidence is up, your bonus clears, and the corner
      office has a wonderful view. Somewhere below, two hundred and fourteen games go dark at once,
      and every copy anyone ever paid for stops working in the same minute. From this height you
      cannot see a single player left holding a receipt for something that no longer exists.`;
  } else {
    body = `At 3:14 AM the shutdown list hits every outlet at once. Names, dates, margins, signatures.
      By morning the Board is testifying and you are unemployed, probably unemployable.
      ${flags.undergroundHelped
        ? 'The community that would not let games die mirrors every doomed build before the kill order lands. Two hundred and fourteen titles outlive the company that owned them.'
        : 'The community works through the night saving what it can still reach.'}
      Somewhere, on an offline machine that needs no server and asks no one's permission, a small
      farm game boots for a nine-year-old. It still works. It will always work. That was the whole point.`;
  }
  ui.showScreen(`
    <div class="screen-bg" style="background-image:url('assets/art/${art}.webp')"></div>
    <div class="screen-inner">
      <div class="screen-h">${title}</div>
      <p class="screen-p" style="max-width:520px;margin:0 auto 20px">${body}</p>
      <p class="screen-p dim">FINAL LEGACY SCORE: <strong style="color:var(--accent)">${score}</strong></p>
      <button class="big-btn primary" id="btn-credits">View Patch Notes</button>
    </div>
  `);
  document.querySelector('#btn-credits')?.addEventListener('click', onContinue);
}

export function creditsScreen(
  ui: UI,
  stats: RunStats,
  flags: RunFlags,
  ending: EndingId,
  score: number,
  board: ScoreEntry[],
  onRestart: () => void,
): void {
  const endingName = ending === 'whistleblower' ? 'Whistleblower' : ending === 'loyal' ? 'Loyal Executive' : 'Terminated';
  const lines: Array<[string, string]> = [
    ['pn-fix', `Reviewed ${stats.processed} licenses against the profitability floor (${stats.correctCount} to Board spec)`],
    ['pn-del', `Sunset ${stats.sunsetCount} games. Every copy anyone paid for stopped working`],
    ['pn-add', `Greenlit ${stats.greenlitCount} titles for one more quarter of breathing`],
    ['pn-fix', `Cross-checked ${stats.discrepanciesFound} discrepancies for a reason to say no`],
    ['', `Logged ${stats.mistakes} board-visible mistakes`],
    ['pn-add', `Banked ${stats.creditsEarned} CR while nobody kept the things they bought`],
  ];
  if (stats.auditsCorrect > 0) lines.push(['pn-fix', `Escalated ${stats.auditsCorrect} fraud case(s) to Legal`]);
  if (stats.bribesTaken > 0) lines.push(['pn-del', `Accepted ${stats.bribesTaken} unmarked envelope(s). Integrity: removed`]);
  if (flags.journalistTip) lines.push(['pn-add', 'Tipped off one (1) journalist']);
  if (flags.undergroundHelped) lines.push(['pn-add', 'Preserved: STARLIGHT VIGIL and 8,000 players\' sky']);
  else if (flags.undergroundAgreed) lines.push(['pn-del', 'Told the Underground you would think about it. That was the entire contribution']);
  if (flags.leakTaken) lines.push(['pn-add', 'Leaked: the shutdown list (214 titles) to the press']);
  lines.push(['', 'Reminder to self: the players owned none of them. Neither do you.']);
  lines.push(['', 'Known issue: conscience. Will not fix.']);
  lines.push(['pn-add', 'This build runs forever. No server required, no permission asked. No sunset possible.']);

  ui.showScreen(`
    <div class="screen-inner">
      <div class="screen-h">PATCH NOTES</div>
      <div class="patchnotes">
        <h3>THE BOTTOM LINE · CAREER BUILD v1.0.${stats.processed}</h3>
        <div class="pn-ver">ENDING: ${endingName} · LEGACY SCORE ${score} · generated locally from your run, no network call</div>
        <ul>${lines.map(([cls, text]) => `<li class="${cls}">${esc(text)}</li>`).join('')}</ul>
      </div>
      <div class="leaderboard">
        <div class="panel-title" style="text-align:center">HIGH SCORE CHASERS · LOCAL BOARD</div>
        ${board.length === 0 ? '<div class="lb-row"><span>No careers on file</span></div>' : board.map((entry) => `
          <div class="lb-row ${entry.score === score && entry.ending === endingName ? 'you' : ''}">
            <span>${esc(entry.ending)}</span><span>${entry.score}</span>
          </div>`).join('')}
      </div>
      <button class="big-btn primary" id="btn-restart">New Career</button>
    </div>
  `);
  document.querySelector('#btn-restart')?.addEventListener('click', onRestart);
}
